/**
 * GxNavSheet (mobile pass, screens §2): the menu on a phone. A full-height sheet that holds what the rail and the top bar's right side hold
 * on a laptop: the nav rows, Coach, the round's steps and the account. Mounted only while open. The facilitator frame passes its own rows
 * and its own extras (the room screen, the pre/post-check line) as children.
 */
import { useEffect, useRef, type ReactNode } from "react";
import { GxSheet } from "../theme/GxSheet";
import { Icon } from "../theme/icons";
import { useCoach } from "../coach/CoachDrawer";
import type { MenuItem } from "../theme/Menu";
import { coachScreenOf, type RailItem } from "./Rail";
import { GxGuideList } from "./RoundGuide";
import type { RoundGuide } from "./guide";
import { Link, useLocation } from "./history";
import { roleLabel } from "./TopBar";

export const NAV_SHEET_ID = "gx-navsheet";

export function GxNavSheet({ onClose, bankName, subline, role, acting = null, items, active, badges = {}, coach = null, guide = null, name, email = null, accountItems = [], onSignOut, children }: {
  readonly onClose: () => void;
  readonly bankName: string; readonly subline: string; readonly role: string | null;
  /** The acting-as chip (a hand-over is live), so a shared phone says whose seat it is. */
  readonly acting?: ReactNode;
  readonly items: readonly RailItem[]; readonly active: string;
  /** A small marker on a row, by item key: the Outlook dot, the facilitator's queue count. */
  readonly badges?: Readonly<Record<string, ReactNode>>;
  /** Coach is offered with the screen it would look at (`active`); null leaves it out (the facilitator has no coach). */
  readonly coach?: { readonly active: string } | null;
  readonly guide?: RoundGuide | null;
  readonly name: string; readonly email?: string | null; readonly accountItems?: readonly MenuItem[]; readonly onSignOut?: () => void;
  /** Extra sections between the nav rows and the account (the facilitator's room screen and pre/post-check line). */
  readonly children?: ReactNode;
}) {
  const openCoach = useCoach().open;
  // Moving to another address (a step that is a link, a row) leaves the sheet behind.
  const { pathname, search } = useLocation();
  const at = useRef(pathname + search);
  useEffect(() => { if (at.current !== pathname + search) onClose(); }, [pathname, search, onClose]);
  return (
    <GxSheet title="Menu" variant="full" onClose={onClose} id={NAV_SHEET_ID} testId="shell.navsheet">
      <div className="gx-navsheet">
        <header className="gx-navsheet-id">
          <b>{bankName}</b>
          <span>{subline === "" ? "" : subline}{role === null ? null : <span className="gx-role gx-navsheet-role">{roleLabel(role)}</span>}</span>
          {acting}
        </header>
        {items.length === 0 ? null : <nav aria-label="Main" className="gx-navsheet-nav">
          {items.map((it) => (
            <Link key={it.key} href={it.href} className="gx-nav" aria-current={active === it.key ? "page" : undefined} onClick={onClose}>
              <Icon name={it.icon} size={20} /><span className="gx-nav-l">{it.label}</span>{badges[it.key] ?? null}
            </Link>
          ))}
        </nav>}
        {coach === null ? null : (
          <section aria-label="Support" className="gx-navsheet-sec">
            <button type="button" className="gx-nav" data-testid="shell.navsheet.coach" onClick={() => { onClose(); openCoach(coachScreenOf(coach.active)); }}>
              <Icon name="coach" size={20} /><span className="gx-nav-l">Coach</span><span className="gx-badge gx-badge--neutral gx-nav-ask">Ask</span>
            </button>
          </section>
        )}
        {guide === null ? null : (
          <section aria-label="Round steps" className="gx-navsheet-sec" data-testid="shell.navsheet.guide">
            <h3 className="gx-eyebrow gx-navsheet-h">Your round</h3>
            <GxGuideList guide={guide} sheet onNavigate={onClose} />
          </section>
        )}
        {children}
        <section aria-label="Account" className="gx-navsheet-sec gx-navsheet-acct">
          {name === "" ? null : <div className="gx-navsheet-who"><b>{name}</b>{email === null ? null : <span>{email}</span>}</div>}
          {[...accountItems, ...(onSignOut === undefined ? [] : [{ key: "signout", label: "Sign out", onSelect: onSignOut }])].map((it) => (
            <button key={it.key} type="button" className="gx-nav" data-testid={`shell.navsheet.${it.key}`} onClick={() => { onClose(); it.onSelect(); }}>
              <span className="gx-nav-l">{it.label}</span>
            </button>
          ))}
        </section>
      </div>
    </GxSheet>
  );
}
